import React, { ReactNode, useContext, createContext, useRef, useEffect } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

import {
  CheckboxFilterOption,
  FilterBoxTypes,
  FilterOption,
} from '@/src/components/common/FilterBox/V2/FilterBox';
import { Task } from '@/src/components/myTasks2/components/Tabs/MyPortfolio/types/task';
import { useNetworkStatus } from '@/src/components/project/Processes2/hooks/useNetworkStatus';
import { TaskStatusEnum } from '@/src/components/task/Config';
import { SortOrder } from '@/src/util/filters';

import { FilterOptionsResponse } from '../api/getFilterOptions';
import {
  COLUMNS,
  MyPortfolioColumns,
  DEFAULT_SORTING,
} from '../components/Category/Tasks/Table/Table';
import { MyPortfolioColumnsEnum, MyPortfolioViewEnum } from '../types';

export type TaskPrefilters = {
  workstreamName: 'string';
};

export type MyPortfolioFilterOption = {
  [key in MyPortfolioColumnsEnum]: FilterOption;
};
export type UpdateFilterOptionsDTO = {
  columnKey: MyPortfolioColumnsEnum;
  filterOption: FilterOption;
};

export type UpdateSortOptionsDTO = {
  column: MyPortfolioColumnsEnum;
  order: SortOrder;
};

export type SocketData = {
  taskIds: number[];
  projectId: number;
  userId: number;
};

export type TaskSocketData = {
  updated: SocketData['taskIds'];
  deleted: SocketData['taskIds'];
  created: SocketData['taskIds'];
};

export type TreatmentZustandStore = {
  columns: MyPortfolioColumns;
  enabledColumns: MyPortfolioColumns;
  filterOptions: MyPortfolioFilterOption;
  responseFilterOptions: FilterOptionsResponse;
  sorting: UpdateSortOptionsDTO;
  initializeFilterOptions: (responseFilterOptions: FilterOptionsResponse) => void;
  resetFilterOptionsAndSorting: () => void;
  updateFilterOptions: (props: UpdateFilterOptionsDTO) => void;
  updateStatusFilterOption: (props: UpdateFilterOptionsDTO & { completed: boolean }) => void;
  updateSorting: (newSorting: UpdateSortOptionsDTO) => void;
  updateColumns: (columns: MyPortfolioColumns) => void;
  toggleColumnEnabled: (columnKey: MyPortfolioColumnsEnum) => void;
  completed: boolean;
  listViewType: string;
  setListViewType: (type: string) => void;
  tabViewType: MyPortfolioViewEnum;
  setTabViewType: (type: MyPortfolioViewEnum) => void;
  // Global calendar states
  selectedWeekRange: null | { start: Date; end: Date };
  setSelectedWeekRange: (range: { start: Date; end: Date } | null) => void;
  weekRange: null | { start: Date; end: Date };
  setWeekRange: (range: { start: Date; end: Date } | null) => void;
  visibleCount: number;
  setVisibleCount: (count: number) => void;
  selectedTask: null | Task;
  setSelectedTask: (task: Task | null) => void;
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
  loadMoreLoading: boolean;
  setLoadMoreLoading: (loading: boolean) => void;
  socketData: TaskSocketData;
  setSocketData: (socketData: TaskSocketData) => void;
  resetSocketData: () => void;
};

export function checkAtleastOneCheckboxFilterAllUnselected(filterOptions: MyPortfolioFilterOption) {
  return Object.values(filterOptions).some(
    (obj) => obj.type === FilterBoxTypes.Checkbox && obj.value.every((val) => !val.isActive)
  );
}

export function getFilterOption<T extends MyPortfolioColumnsEnum>(
  filterOptions: MyPortfolioFilterOption,
  columnKey: T
): MyPortfolioFilterOption[T] {
  return filterOptions[columnKey];
}

export const getInitialFilterOptions = (
  responseFilterOptions: FilterOptionsResponse,
  prefilters: TaskPrefilters,
  columns: MyPortfolioColumns,
  stateFilterOptions?: MyPortfolioFilterOption
) => {
  const initialFilterOptions: MyPortfolioFilterOption = {} as MyPortfolioFilterOption;

  // Build checkbox options
  Object.keys(responseFilterOptions).forEach((key) => {
    if (key === MyPortfolioColumnsEnum.TaskStatus && stateFilterOptions?.[key]) {
      initialFilterOptions[key] = stateFilterOptions[key];
      return;
    }

    // const uniqueTrimmedFilterOptions = [
    //   ...new Set<string>(responseFilterOptions[key].map((value) => value.trim())),
    // ];

    const options = responseFilterOptions[key].map((value) => {
      if (typeof value === 'string') {
        return {
          id: value.toLowerCase().replace(/\s/g, '-'),
          label: value,
          key: value,
          value: value,
          isActive: true, // Initial is all true
        };
      }
      return {
        id: value.id,
        label: value.val,
        key: value.id,
        value: value.id,
        isActive: true, // Initial is all true
      };
    });

    initialFilterOptions[key as MyPortfolioColumnsEnum] = {
      id: key,
      type: FilterBoxTypes.Checkbox,
      value: options,
      isActive: prefilters && prefilters[key],
    };
  });

  // Handle taskStatus separately if it's not already in stateFilterOptions
  const stateFilterTaskStatus = stateFilterOptions?.[
    MyPortfolioColumnsEnum.TaskStatus
  ] as CheckboxFilterOption;

  if (!stateFilterTaskStatus || stateFilterTaskStatus.value.length < 1) {
    initialFilterOptions.taskStatus = {
      id: MyPortfolioColumnsEnum.TaskStatus,
      type: FilterBoxTypes.Checkbox,
      value: responseFilterOptions.taskStatus.map((status) => {
        return {
          id: status.toLowerCase().replace(/\s/g, '-'),
          label: status,
          key: status,
          value: status,
          isActive: status === TaskStatusEnum.Completed ? false : true, // Initial is all true
        };
      }),
      isActive: true,
    };
  } else {
    initialFilterOptions.taskStatus = stateFilterOptions.taskStatus;
  }

  // Build textContaining options
  initialFilterOptions.taskName = {
    id: FilterBoxTypes.TextContaining, // Used by legacy as type
    type: FilterBoxTypes.TextContaining,
    value: '',
    isActive: false,
  };

  initialFilterOptions.projectName = {
    ...initialFilterOptions.projectName,
    isActive: true,
  };

  initialFilterOptions.assigneeName = {
    ...initialFilterOptions.assigneeName,
    isActive: true,
  };

  initialFilterOptions.isMilestone = {
    id: MyPortfolioColumnsEnum.IsMilestone,
    type: FilterBoxTypes.Boolean,
    isActive: false,
    value: true,
  };

  initialFilterOptions.hasReport = {
    id: MyPortfolioColumnsEnum.HasReport,
    type: FilterBoxTypes.Boolean,
    isActive: false,
    value: true,
  };

  // Build date options
  Object.keys(columns).forEach((key) => {
    const column = columns[key];

    if (column.filterType === FilterBoxTypes.DateRange) {
      initialFilterOptions[key as MyPortfolioColumnsEnum] = {
        id: FilterBoxTypes.DateRange, //  Used by legacy as type,
        type: FilterBoxTypes.DateRange,
        value: {
          from: null,
          to: null,
        },
        isActive: false,
      };
    }
  });

  initialFilterOptions.isMilestone = {
    id: MyPortfolioColumnsEnum.IsMilestone,
    type: FilterBoxTypes.Boolean,
    isActive: false,
    value: true,
  };

  return initialFilterOptions;
};

export type MyPortfolioStore = TreatmentZustandStore & {
  // onDraftAction: () => void;
};

const getDefaultEnabledColumns = (columns) => {
  const enabledColumns: MyPortfolioColumns = {};

  Object.keys(columns).forEach((key) => {
    const column = columns[key];

    if (column.default) {
      enabledColumns[key as MyPortfolioColumnsEnum] = column;
    }
  });

  return enabledColumns;
};

const createMyPortfoliotore = ({
  columns,
  prefilters,
}: {
  columns: MyPortfolioColumns;
  prefilters: TaskPrefilters;
}) => {
  const DEFAULT_PROPS = {
    columns,
    completed: false,
    listViewType: 'calendar',
    tabViewType: MyPortfolioViewEnum.Workstream,
  };

  return createStore<TreatmentZustandStore>()((set) => ({
    ...DEFAULT_PROPS,
    filterOptions: null,
    responseFilterOptions: null,
    sorting: DEFAULT_SORTING,
    enabledColumns: getDefaultEnabledColumns(DEFAULT_PROPS.columns),
    initializeFilterOptions: (responseFilterOptions) =>
      set((state) => {
        const initialFilterOptions = getInitialFilterOptions(
          responseFilterOptions,
          prefilters,
          DEFAULT_PROPS.columns,
          state.filterOptions
        );

        return {
          filterOptions: initialFilterOptions,
          responseFilterOptions,
        };
      }),
    updateFilterOptions: ({ columnKey, filterOption }) =>
      set((state) => {
        return {
          filterOptions: {
            ...state.filterOptions,
            [columnKey]: {
              ...state.filterOptions[columnKey],
              ...filterOption,
            },
          },
        };
      }),
    updateStatusFilterOption: ({ columnKey, filterOption, completed }) =>
      set((state) => {
        return {
          filterOptions: {
            ...state.filterOptions,
            [columnKey]: {
              ...state.filterOptions[columnKey],
              ...filterOption,
            },
          },
          completed,
        };
      }),
    updateSorting: (newSorting) =>
      set(() => {
        return {
          sorting: newSorting,
        };
      }),
    toggleColumnEnabled: (columnKey) =>
      set((state) => {
        const newColumns = {
          ...state.columns,
          [columnKey]: {
            ...state.columns[columnKey],
            enabled: !state.columns[columnKey].enabled,
          },
        };
        const newEnabledColumns: MyPortfolioColumns = Object.keys(newColumns)
          .filter((key) => newColumns[key].enabled)
          .reduce((acc, key) => {
            acc[key as MyPortfolioColumnsEnum] = newColumns[key as MyPortfolioColumnsEnum];
            return acc;
          }, {} as MyPortfolioColumns);

        return {
          columns: newColumns,
          enabledColumns: newEnabledColumns,
        };
      }),
    updateColumns: (columns) =>
      set(() => {
        const newEnabledColumns: MyPortfolioColumns = Object.keys(columns)
          .filter((key) => columns[key].enabled)
          .reduce((acc, key) => {
            acc[key as MyPortfolioColumnsEnum] = columns[key as MyPortfolioColumnsEnum];
            return acc;
          }, {} as MyPortfolioColumns);

        return {
          columns,
          enabledColumns: newEnabledColumns,
        };
      }),
    resetFilterOptionsAndSorting: () =>
      set((state) => {
        return {
          filterOptions: getInitialFilterOptions(
            state.responseFilterOptions,
            prefilters,
            DEFAULT_PROPS.columns
          ),
          sorting: DEFAULT_SORTING,
          completed: false,
        };
      }),
    setListViewType: (type: string) => set(() => ({ listViewType: type })),
    setTabViewType: (type: MyPortfolioViewEnum) => set(() => ({ tabViewType: type })),

    // Global calendar states
    selectedWeekRange: null,
    setSelectedWeekRange: (range) => set(() => ({ selectedWeekRange: range })),
    weekRange: null,
    setWeekRange: (range) => set(() => ({ weekRange: range })),
    visibleCount: 0,
    setVisibleCount: (count) => set(() => ({ visibleCount: count })),
    selectedTask: null,
    setSelectedTask: (task) => set(() => ({ selectedTask: task })),
    isOffline: false,
    setIsOffline: (offline) => set(() => ({ isOffline: offline })),
    loadMoreLoading: false,
    setLoadMoreLoading: (loading) => set(() => ({ loadMoreLoading: loading })),
    socketData: {
      created: [],
      updated: [],
      deleted: [],
    },
    setSocketData: (updatedSocketData) =>
      set(() => {
        return {
          socketData: updatedSocketData,
        };
      }),
    resetSocketData: () =>
      set(() => {
        return {
          socketData: {
            created: [],
            updated: [],
            deleted: [],
          },
        };
      }),
  }));
};

export const MyPortfolioContext = createContext<MyPortfolioStore>(undefined);

export type MyPortfolioContextProviderProps = {
  children: ReactNode;
  columns?: MyPortfolioColumns;
  prefilters?: TaskPrefilters;
};

export const MyPortfolioContextProvider = ({ children, columns = COLUMNS, prefilters = null }) => {
  const storeRef = useRef<StoreApi<TreatmentZustandStore>>();
  if (!storeRef.current) {
    storeRef.current = createMyPortfoliotore({
      columns,
      prefilters,
    });
  }
  const store = useStore(storeRef.current);

  const isOffline = useNetworkStatus();
  useEffect(() => {
    store.setIsOffline(isOffline);
  }, [isOffline]);

  return (
    <MyPortfolioContext.Provider
      value={{
        ...store,
      }}
    >
      {children}
    </MyPortfolioContext.Provider>
  );
};

export const useMyPortfolio = (): MyPortfolioStore => {
  const context = useContext(MyPortfolioContext);
  if (!context) {
    throw new Error('useMyPortfolio must be used within a MyPortfolioContextProvider');
  }
  return context;
};
